import { create } from 'zustand';
import type { HabitSchedule } from '../types';

/**
 * ⭐ "PICK THE DAYS" FOR PATHS WITH NO FORM (owner, 2026-10-04).
 *
 * AI suggestions, the AI coach, quest proposals and shared-item imports used to
 * create a weekly / monthly / yearly habit with NO day — never due, so never doable
 * (utils/habitSchedule.ts). The owner's call: those paths OPEN A FORM so the person
 * picks. This store is the queue behind that form: code `await`s a schedule, and
 * `HabitDaysPrompt` (mounted once in App) shows one request at a time.
 *
 * ⚠️ It RETURNS the schedule; it never creates the habit. That is what lets a path
 * ask for every day FIRST and only then charge Coin and create (owner, 2026-10-04:
 * "ask for the days first, then charge"). `null` = the person cancelled.
 */
export interface HabitDaysRequest {
  id: string;
  /** The habit's name, shown in the dialog's title. */
  name: string;
  /** The frequency to complete — its days list may be missing or empty. */
  schedule: HabitSchedule;
  resolve: (schedule: HabitSchedule | null) => void;
}

interface HabitDaysRequestState {
  queue: HabitDaysRequest[];
  /** Answers the request at the front of the queue and shows the next one. */
  answer: (id: string, schedule: HabitSchedule | null) => void;
}

export const useHabitDaysRequestStore = create<HabitDaysRequestState>()((set, get) => ({
  queue: [],
  answer: (id, schedule) => {
    const request = get().queue.find(r => r.id === id);
    if (!request) return;
    set(state => ({ queue: state.queue.filter(r => r.id !== id) }));
    request.resolve(schedule);
  },
}));

/** Ask the person to pick the days for one habit. Resolves `null` if they cancel. */
export function askHabitDays(name: string, schedule: HabitSchedule): Promise<HabitSchedule | null> {
  return new Promise(resolve => {
    useHabitDaysRequestStore.setState(state => ({
      queue: [...state.queue, { id: crypto.randomUUID(), name, schedule, resolve }],
    }));
  });
}
